import { useEffect, useState } from "react";
import Icon from "../base/Icon";
import Select from "./Select";
import { API_ROUTES } from "../../core/Constants";
import { useHub } from "../../core/Global";
import PhoneLineSelectItem from "../../models/PhoneLineSelectItem";
import "./PhoneNumberSelect.css";


// The single phone-number picker. A bare list of numbers says nothing about which one to
// pick, so every row carries where the number is -- or that it is toll-free, which has no
// where -- and the job source behind it.
//
// A single-select <Select> posts no form control of its own, so the caller holds the value
// and merges it into whatever it submits; `name` is still passed so the field's error span
// is where window.formError expects to find it.
interface IProps {
    id?: string
    // The form field this stands in for. Also where a server-side field error lands.
    name?: string
    label?: string
    labelUp?: boolean
    block?: boolean
    small?: boolean
    marginBottom?: number
    placeholder?: string
    disabled?: boolean
    visible?: boolean
    deselect?: boolean

    value?: string
    onChange: (lineId?: string) => void

    // Defaults to the account's own numbers. Overridden where a screen needs a different set
    // -- the system-admin coupon sender, for one, which spans every account.
    apiUrl?: string

    // Picking the first number is right where one has to be chosen to proceed, and wrong where
    // leaving it empty means "no override".
    autoSelectFirst?: boolean

    // For a picker choosing who a message goes out as: the account's default sender leads the
    // list and says so. Off where the choice is not a sender -- a source's default line, or an
    // extension's outbound line, has nothing to do with which number sends the messages.
    markDefaultSender?: boolean
}
export default function PhoneNumberSelect(props: IProps) {

    const hub = useHub()

    const [numbers, numbersSet] = useState<PhoneLineSelectItem[]>([]);

    useEffect(() => {
        hub.Get(props.apiUrl ?? API_ROUTES.SelectPhoneNumbers, res => {
            let items: PhoneLineSelectItem[] = res.data ?? [];

            // The default sender leads, so it is both the first thing read and what
            // autoSelectFirst lands on. The rest keep the order they arrived in.
            if (props.markDefaultSender) {
                items = [...items].sort((a, b) => Number(b.defaultSmsSender) - Number(a.defaultSmsSender));
            }

            numbersSet(items);
            if (props.autoSelectFirst && items.length > 0 && !props.value) props.onChange(items[0].id);
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.apiUrl])

    const details = (item: PhoneLineSelectItem) => (
        <div className="phone-select-metas">
            {
                item.tollFree ?
                    <span className="phone-select-meta">
                        <Icon icon="phone-volume" size={10} className="me-1" />
                        <span className="phone-select-meta-text">Toll-Free</span>
                    </span>
                    : item.region ?
                        <span className="phone-select-meta">
                            <Icon icon="location-dot" size={10} className="me-1" />
                            <span className="phone-select-meta-text">{item.region}</span>
                        </span>
                        : <></>
            }
            {
                item.sourceName &&
                <span className="phone-select-meta phone-select-meta-source">
                    <Icon icon="bullhorn" type="solid" size={10} className="me-1" />
                    <span className="phone-select-meta-text">{item.sourceName}</span>
                </span>
            }
            {
                props.markDefaultSender && item.defaultSmsSender &&
                <span className="phone-select-meta phone-select-meta-default">
                    <Icon icon="star" type="solid" size={9} className="me-1" />
                    <span className="phone-select-meta-text">Default Sender</span>
                </span>
            }
        </div>
    )

    return (
        <Select id={props.id} name={props.name} label={props.label}
            labelUp={props.labelUp} block={props.block} small={props.small}
            marginBottom={props.marginBottom} disabled={props.disabled} visible={props.visible}
            placeholder={props.placeholder ?? "Choose a number"}
            deselect={props.deselect ?? false}
            source={numbers} value={props.value}
            searchPlaceholder="Search number, region or source..."
            searchText={(item: PhoneLineSelectItem) => [item.name, item.region, item.sourceName, item.tollFree ? "Toll-Free" : "", item.defaultSmsSender ? "Default Sender" : ""].join(" ")}
            optionTemplate={(item: PhoneLineSelectItem) =>
                <div className="phone-select-option">
                    <div className="phone-select-number">{item.name}</div>
                    {details(item)}
                </div>
            }
            selectionTemplate={(item: PhoneLineSelectItem) =>
                <div className="phone-select-selection">
                    <span className="phone-select-number">{item.name}</span>
                    {details(item)}
                </div>
            }
            onSelect={val => props.onChange(val)}
            onClear={() => props.onChange(undefined)} />
    )
}
